import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../_model/api-response';
import {
  ClearHouseMarketMakerEmergencyStopDto,
  DisableHouseMarketMakerLiveExecutionDto,
  EmergencyStopHouseMarketMakerDto,
  EnableHouseMarketMakerLiveExecutionDto,
  HouseCurrencyInventoryTargetDto,
  HouseInventoryTargetsResponseDto,
  HouseMarketMakerDecisionDetailsDto,
  HouseMarketMakerDecisionListItemDto,
  HouseMarketMakerExecutionDetailsDto,
  HouseMarketMakerExecutionListItemDto,
  HouseMarketMakerExecutionReconciliationResult,
  HouseMarketMakerReconciliationBatchResult,
  HouseMarketMakerPairStrategyDto,
  HouseMarketMakerRunDetailsDto,
  HouseMarketMakerRunSummaryDto,
  HouseMarketMakerScanResultDto,
  HouseMarketMakerSettingsDto,
  HouseMarketMakerStatusDto,
  HouseMarketMakerWorkerHealthDto,
  UpdateHouseCurrencyInventoryTargetDto,
  UpdateHouseMarketMakerPairStrategyDto,
  UpdateHouseMarketMakerSettingsDto
} from '../_model/house-market-maker';

@Injectable({ providedIn: 'root' })
export class HouseMarketMakerService {
  private readonly base = `${environment.apiBaseUrl}/admin/house/market-maker`;

  constructor(private readonly http: HttpClient) {}

  getStatus() {
    return this.http.get<ApiResponse<HouseMarketMakerStatusDto>>(`${this.base}/status`);
  }

  getWorkerHealth() {
    return this.http.get<ApiResponse<HouseMarketMakerWorkerHealthDto>>(`${this.base}/worker-health`);
  }

  getSettings() {
    return this.http.get<ApiResponse<HouseMarketMakerSettingsDto>>(`${this.base}/settings`);
  }

  updateSettings(payload: UpdateHouseMarketMakerSettingsDto) {
    return this.http.put<ApiResponse<HouseMarketMakerSettingsDto>>(`${this.base}/settings`, payload);
  }

  getPairStrategies() {
    return this.http.get<ApiResponse<HouseMarketMakerPairStrategyDto[]>>(`${this.base}/pair-strategies`);
  }

  updatePairStrategy(strategyId: string, payload: UpdateHouseMarketMakerPairStrategyDto) {
    return this.http.put<ApiResponse<HouseMarketMakerPairStrategyDto>>(
      `${this.base}/pair-strategies/${encodeURIComponent(strategyId)}`,
      payload
    );
  }

  getInventoryTargets() {
    return this.http.get<ApiResponse<HouseInventoryTargetsResponseDto>>(`${this.base}/inventory-targets`);
  }

  updateInventoryTarget(currency: string, payload: UpdateHouseCurrencyInventoryTargetDto) {
    return this.http.put<ApiResponse<HouseCurrencyInventoryTargetDto>>(
      `${this.base}/inventory-targets/${encodeURIComponent(currency)}`,
      payload
    );
  }

  runShadowScan() {
    return this.http.post<ApiResponse<HouseMarketMakerScanResultDto>>(`${this.base}/run-shadow-scan`, {});
  }

  runLiveScan() {
    return this.http.post<ApiResponse<HouseMarketMakerScanResultDto>>(`${this.base}/run-live-scan`, {});
  }

  enableLiveExecution(payload: EnableHouseMarketMakerLiveExecutionDto) {
    return this.http.post<ApiResponse<HouseMarketMakerSettingsDto>>(`${this.base}/live-execution/enable`, payload);
  }

  disableLiveExecution(payload: DisableHouseMarketMakerLiveExecutionDto) {
    return this.http.post<ApiResponse<HouseMarketMakerSettingsDto>>(`${this.base}/live-execution/disable`, payload);
  }

  emergencyStop(payload: EmergencyStopHouseMarketMakerDto) {
    return this.http.post<ApiResponse<HouseMarketMakerSettingsDto>>(`${this.base}/emergency-stop`, payload);
  }

  clearEmergencyStop(payload: ClearHouseMarketMakerEmergencyStopDto) {
    return this.http.post<ApiResponse<HouseMarketMakerSettingsDto>>(`${this.base}/emergency-stop/clear`, payload);
  }

  getRuns(filters: {
    status?: string;
    trigger?: string;
    page?: number;
    rowsPerPage?: number;
  } = {}) {
    let params = new HttpParams()
      .set('page', String(filters.page || 1))
      .set('rowsPerPage', String(filters.rowsPerPage || 20));

    if (filters.status) params = params.set('status', filters.status);
    if (filters.trigger) params = params.set('trigger', filters.trigger);

    return this.http.get<ApiResponse<HouseMarketMakerRunSummaryDto[]>>(`${this.base}/runs`, { params });
  }

  getRun(runId: string) {
    return this.http.get<ApiResponse<HouseMarketMakerRunDetailsDto>>(
      `${this.base}/runs/${encodeURIComponent(runId)}`
    );
  }

  getDecisions(filters: {
    runId?: string;
    houseCurrencyPairId?: string;
    userOrderId?: string;
    status?: string;
    strategyType?: string;
    currency?: string;
    page?: number;
    rowsPerPage?: number;
  } = {}) {
    let params = new HttpParams()
      .set('page', String(filters.page || 1))
      .set('rowsPerPage', String(filters.rowsPerPage || 20));

    if (filters.runId) params = params.set('runId', filters.runId);
    if (filters.houseCurrencyPairId) params = params.set('houseCurrencyPairId', filters.houseCurrencyPairId);
    if (filters.userOrderId) params = params.set('userOrderId', filters.userOrderId);
    if (filters.status) params = params.set('status', filters.status);
    if (filters.strategyType) params = params.set('strategyType', filters.strategyType);
    if (filters.currency) params = params.set('currency', filters.currency.trim().toUpperCase());

    return this.http.get<ApiResponse<HouseMarketMakerDecisionListItemDto[]>>(`${this.base}/decisions`, { params });
  }

  getDecision(decisionId: string) {
    return this.http.get<ApiResponse<HouseMarketMakerDecisionDetailsDto>>(
      `${this.base}/decisions/${encodeURIComponent(decisionId)}`
    );
  }

  getExecutions(filters: {
    runId?: string;
    decisionId?: string;
    status?: string;
    reconciliationStatus?: string;
    page?: number;
    rowsPerPage?: number;
  } = {}) {
    let params = new HttpParams()
      .set('page', String(filters.page || 1))
      .set('rowsPerPage', String(filters.rowsPerPage || 20));

    if (filters.runId) params = params.set('runId', filters.runId);
    if (filters.decisionId) params = params.set('decisionId', filters.decisionId);
    if (filters.status) params = params.set('status', filters.status);
    if (filters.reconciliationStatus) {
      params = params.set('reconciliationStatus', filters.reconciliationStatus);
    }

    return this.http.get<ApiResponse<HouseMarketMakerExecutionListItemDto[]>>(`${this.base}/executions`, { params });
  }

  getExecution(executionId: string) {
    return this.http.get<ApiResponse<HouseMarketMakerExecutionDetailsDto>>(
      `${this.base}/executions/${encodeURIComponent(executionId)}`
    );
  }


  reconcileExecutions() {
    return this.http.post<ApiResponse<HouseMarketMakerReconciliationBatchResult>>(
      `${this.base}/executions/reconcile`,
      {}
    );
  }

  reconcileExecution(executionId: string) {
    return this.http.post<ApiResponse<HouseMarketMakerExecutionReconciliationResult>>(
      `${this.base}/executions/${encodeURIComponent(executionId)}/reconcile`,
      {}
    );
  }
}
